import { useReducer } from 'react';
import { useNavigate } from 'react-router';

import Button from '@ingka/button';
import chevronDownIcon from '@ingka/ssr-icon/paths/chevron-down';
import chevronUpIcon from '@ingka/ssr-icon/paths/chevron-up';
import type { AssetPreview } from '@repo/api-types/customer-assets';
import { translate } from '@repo/phrase';
import { Paragraph } from '@repo/ui/typography';

import { AssetRow } from '@/components/assets/asset-row.component.tsx';
import { AssetsEmpty } from '@/components/assets/assets-empty';
import { AssetsSkeleton } from '@/components/assets/assets-skeleton';
import { useAssets } from '@/components/assets/assets.hooks';
import { QueryError } from '@/components/query-error';

import { getServiceEndpoint } from '@/api/helpers.api';
import { useAppBaseUrl } from '@/hooks/router.hooks';
import { getAppConfig } from '@/utils/window.utils.ts';

import styles from './styles.module.scss';

const orderByDate = (a: AssetPreview, b: AssetPreview, sortOrder: boolean): number => {
    const dateA = new Date(a.creationDate);
    const dateB = new Date(b.creationDate);

    if (dateA < dateB) {
        return sortOrder ? -1 : 1;
    }
    return 0;
};

const orderAssets = (assets: AssetPreview[], sortOrder: boolean): AssetPreview[] =>
    assets.slice().sort((a, b) => orderByDate(a, b, sortOrder));

const tableHeader = [
    { text: 'asset_list_table_alias', key: crypto.randomUUID() },
    { text: 'asset_list_table_type', key: crypto.randomUUID() },
    { text: 'asset_list_table_id', key: crypto.randomUUID() },
    { text: 'asset_list_table_status', key: crypto.randomUUID() },
    { text: 'asset_list_table_value', key: crypto.randomUUID() },
];

export const Assets = () => {
    const navigate = useNavigate();
    const [order, toggleOrder] = useReducer((prev) => !prev, false);
    const { assetsQuery, assets } = useAssets();
    const config = getAppConfig();
    const storeDetailsBaseUrl = getServiceEndpoint(config.api.services.classicSalja, 'storeDetails');

    const baseUrl = useAppBaseUrl();

    if (assetsQuery.isPending) {
        return <AssetsSkeleton items={10} />;
    }
    if (assetsQuery.isError && !assetsQuery.data) {
        return <QueryError textKey="assets_error_fetching" error={assetsQuery.error} />;
    }
    if (assets.length === 0) {
        return <AssetsEmpty />;
    }

    const viewDetail = (type: string, refId: string) => {
        void navigate(`${baseUrl}/view/${type}/${refId}`);
    };

    return (
        <>
            <div className={styles.counter}>
                <Paragraph>
                    {translate('asset_list_total_number', {
                        number: assets.length.toString(),
                    })}
                </Paragraph>
            </div>
            <div className={styles.assets_table}>
                <div className={styles.assets_table_header}>
                    <div>
                        <Button
                            text={translate('asset_list_table_creation_date')}
                            ssrIcon={order ? chevronUpIcon : chevronDownIcon}
                            onClick={toggleOrder}
                            iconPosition="trailing"
                            type="tertiary"
                            size="small"
                        />
                    </div>
                    {tableHeader.map((header) => (
                        <div key={header.key}>
                            <Paragraph bodySize="s">{translate(header.text)}</Paragraph>
                        </div>
                    ))}
                </div>
                <div id="assets-table-rows">
                    {orderAssets(assets, order).map((asset, key) => (
                        <AssetRow
                            key={asset.refId}
                            asset={asset}
                            assetIndex={key}
                            storeDetailsBaseUrl={storeDetailsBaseUrl}
                            viewDetail={viewDetail}
                        />
                    ))}
                </div>
            </div>
        </>
    );
};
